import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, CalendarX, CheckCircle2, Clock, Loader2, UserPlus, UserX } from 'lucide-react';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { apiClient } from '@/lib/api';

interface RequiredSkill {
  skill_id: string;
  name: string;
  min_rank: number;
}

interface EligibilityCandidate {
  user_id: string;
  eligible: boolean;
  missing_skills: string[];
  required_skills_satisfied: boolean;
  exclusion_reason?: string | null;
  reasons?: string[];
  attendance_state?: string;
}

interface EligibleResourcesResponse {
  required_skills: RequiredSkill[];
  candidates: EligibilityCandidate[];
}

interface UserOption {
  id: string;
  name: string;
}

interface Props {
  serviceId?: string;
  assigneeId?: string;
  startTime?: string;
  endTime?: string;
  storeId?: string;
  users?: UserOption[];
  onSelectAssignee?: (userId: string) => void;
}

type ExclusionKind = 'conflict' | 'off_shift' | 'time_off' | 'attendance' | 'other';

interface ExclusionInfo {
  kind: ExclusionKind;
  label: string;
  title: string;
}

function classifySingle(reason: string): ExclusionInfo | null {
  const r = reason.toLowerCase();
  if (r.startsWith('skill mancanti')) return null; // skills handled separately
  if (r.includes('conflitto')) {
    return { kind: 'conflict', label: 'In conflitto con altro appuntamento', title: reason };
  }
  if (r.includes('ferie') || r.includes('permesso')) {
    return { kind: 'time_off', label: 'In ferie/permesso', title: reason };
  }
  if (r.includes('non in turno') || r.includes('fuori turno')) {
    return { kind: 'off_shift', label: 'Fuori turno', title: reason };
  }
  if (r.includes('stato') || r.includes('walk-in') || r.includes('attendance')) {
    return { kind: 'attendance', label: 'Non in attendance', title: reason };
  }
  return null;
}

function classifyExclusion(
  reason: string | null | undefined,
  reasons?: string[] | null
): ExclusionInfo | null {
  const direct = reason ? classifySingle(reason) : null;
  if (direct) return direct;
  if (Array.isArray(reasons)) {
    for (const r of reasons) {
      if (typeof r !== 'string') continue;
      const lower = r.toLowerCase();
      // Skip "Override: ..." reasons — those describe permitted overrides, not exclusions
      if (lower.startsWith('override')) continue;
      const matched = classifySingle(r);
      if (matched) return matched;
    }
  }
  if (reason) return { kind: 'other', label: reason, title: reason };
  return null;
}

export default function AssigneeSkillsBadge({
  serviceId,
  assigneeId,
  startTime,
  endTime,
  storeId,
  users,
  onSelectAssignee,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [missing, setMissing] = useState<string[] | null>(null);
  const [hasRequired, setHasRequired] = useState(false);
  const [notEvaluable, setNotEvaluable] = useState(false);
  const [exclusion, setExclusion] = useState<ExclusionInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [alternatives, setAlternatives] = useState<string[]>([]);
  const [loadingAlternatives, setLoadingAlternatives] = useState(false);
  const reqIdRef = useRef(0);
  const altReqIdRef = useRef(0);

  const usersById = useMemo(() => {
    const m = new Map<string, string>();
    (users || []).forEach(u => { if (u.id) m.set(u.id, u.name); });
    return m;
  }, [users]);

  useEffect(() => {
    if (!serviceId || !assigneeId || !startTime || !endTime) {
      setMissing(null);
      setHasRequired(false);
      setExclusion(null);
      setError(null);
      setLoading(false);
      setAlternatives([]);
      return;
    }
    const sd = new Date(startTime);
    const ed = new Date(endTime);
    if (Number.isNaN(sd.getTime()) || Number.isNaN(ed.getTime()) || ed <= sd) {
      setMissing(null);
      setHasRequired(false);
      setNotEvaluable(false);
      setExclusion(null);
      setError(null);
      setLoading(false);
      setAlternatives([]);
      return;
    }

    const myReq = ++reqIdRef.current;
    setLoading(true);
    setError(null);

    const handle = setTimeout(async () => {
      try {
        const res = await apiClient.post<EligibleResourcesResponse>(
          '/calendar/eligible-resources',
          {
            service_id: serviceId,
            start: sd.toISOString(),
            end: ed.toISOString(),
            store_id: storeId || null,
            candidate_user_ids: [assigneeId],
          }
        );
        if (reqIdRef.current !== myReq) return;
        if (res.success && res.data) {
          const required = res.data.required_skills || [];
          setHasRequired(required.length > 0);
          const cand = (res.data.candidates || []).find(c => c.user_id === assigneeId);
          if (!cand) {
            setNotEvaluable(true);
            setMissing(null);
            setExclusion(null);
          } else {
            setNotEvaluable(false);
            if (cand.required_skills_satisfied === false) {
              setMissing(cand.missing_skills || []);
            } else {
              setMissing([]);
            }
            setExclusion(classifyExclusion(cand.exclusion_reason, cand.reasons));
          }
        } else {
          setError('Verifica skill non disponibile');
          setMissing(null);
          setNotEvaluable(false);
          setExclusion(null);
        }
      } catch (e) {
        if (reqIdRef.current !== myReq) return;
        setError((e as { response?: { data?: { error?: { message?: string; code?: string } | string; message?: string }; status?: number }; message?: string; code?: string; status?: number })?.message || 'Errore verifica skill');
        setMissing(null);
        setNotEvaluable(false);
        setExclusion(null);
      } finally {
        if (reqIdRef.current === myReq) setLoading(false);
      }
    }, 250);

    return () => clearTimeout(handle);
  }, [serviceId, assigneeId, startTime, endTime, storeId]);

  const needsAlternatives = !loading && ((missing && missing.length > 0) || notEvaluable);

  useEffect(() => {
    if (!needsAlternatives || !serviceId || !startTime || !endTime) {
      setAlternatives([]);
      setLoadingAlternatives(false);
      return;
    }
    const sd = new Date(startTime);
    const ed = new Date(endTime);
    if (Number.isNaN(sd.getTime()) || Number.isNaN(ed.getTime()) || ed <= sd) {
      setAlternatives([]);
      return;
    }

    const myReq = ++altReqIdRef.current;
    setLoadingAlternatives(true);
    const handle = setTimeout(async () => {
      try {
        const res = await apiClient.post<EligibleResourcesResponse>(
          '/calendar/eligible-resources',
          {
            service_id: serviceId,
            start: sd.toISOString(),
            end: ed.toISOString(),
            store_id: storeId || null,
          }
        );
        if (altReqIdRef.current !== myReq) return;
        if (res.success && res.data) {
          const eligibleIds = (res.data.candidates || [])
            .filter(c => c.eligible && c.user_id !== assigneeId)
            .map(c => c.user_id)
            .slice(0, 3);
          setAlternatives(eligibleIds);
        } else {
          setAlternatives([]);
        }
      } catch {
        if (altReqIdRef.current !== myReq) return;
        setAlternatives([]);
      } finally {
        if (altReqIdRef.current === myReq) setLoadingAlternatives(false);
      }
    }, 300);

    return () => clearTimeout(handle);
  }, [needsAlternatives, serviceId, startTime, endTime, storeId, assigneeId]);

  if (!serviceId || !assigneeId || !startTime || !endTime) return null;

  if (loading) {
    return (
      <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
        <Loader2 className="w-3 h-3 animate-spin" />
        <span>Verifica disponibilità…</span>
      </div>
    );
  }

  if (error) {
    return (
      <Badge variant="outline" className="text-xs border-gray-300 text-gray-500">
        {error}
      </Badge>
    );
  }

  const renderAlternatives = () => {
    if (!needsAlternatives) return null;
    if (loadingAlternatives) {
      return (
        <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 mt-1">
          <Loader2 className="w-3 h-3 animate-spin" />
          <span>Cerco alternative…</span>
        </div>
      );
    }
    if (alternatives.length === 0) return null;
    return (
      <div className="flex items-center flex-wrap gap-1 text-xs mt-1 text-gray-600 dark:text-gray-300">
        <UserPlus className="w-3 h-3 text-indigo-500" />
        <span>Disponibili:</span>
        {alternatives.map((id, idx) => {
          const name = usersById.get(id) || id.slice(0, 8);
          return (
            <span key={id} className="inline-flex items-center">
              {onSelectAssignee ? (
                <button
                  type="button"
                  onClick={() => onSelectAssignee(id)}
                  className="text-indigo-600 dark:text-indigo-300 hover:underline focus:outline-none focus:underline"
                  title={`Assegna a ${name}`}
                >
                  {name}
                </button>
              ) : (
                <span>{name}</span>
              )}
              {idx < alternatives.length - 1 && <span className="text-gray-400">,&nbsp;</span>}
            </span>
          );
        })}
      </div>
    );
  };

  if (notEvaluable) {
    return (
      <div>
        <Badge
          variant="outline"
          className="text-xs border-amber-300 bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700 inline-flex items-center gap-1"
          title="La risorsa non rientra nei filtri correnti (es. sede)"
        >
          <AlertCircle className="w-3 h-3" />
          Risorsa non valutabile per i filtri correnti
        </Badge>
        {renderAlternatives()}
      </div>
    );
  }

  const badges: JSX.Element[] = [];

  if (missing && missing.length > 0) {
    badges.push(
      <Badge
        key="missing-skills"
        variant="outline"
        className="text-xs border-red-300 bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700 inline-flex items-center gap-1"
        title={`Skill mancanti: ${missing.join(', ')}`}
      >
        <AlertCircle className="w-3 h-3" />
        Skill mancanti: {missing.join(', ')}
      </Badge>
    );
  }

  if (exclusion) {
    const styleByKind: Record<ExclusionKind, string> = {
      conflict:
        'border-red-300 bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700',
      off_shift:
        'border-amber-300 bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700',
      time_off:
        'border-amber-300 bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700',
      attendance:
        'border-amber-300 bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700',
      other:
        'border-amber-300 bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700',
    };
    const IconByKind: Record<ExclusionKind, typeof AlertCircle> = {
      conflict: CalendarX,
      off_shift: Clock,
      time_off: CalendarX,
      attendance: UserX,
      other: AlertCircle,
    };
    const Icon = IconByKind[exclusion.kind];
    badges.push(
      <Badge
        key="exclusion"
        variant="outline"
        className={`text-xs inline-flex items-center gap-1 ${styleByKind[exclusion.kind]}`}
        title={exclusion.title}
      >
        <Icon className="w-3 h-3" />
        {exclusion.label}
      </Badge>
    );
  }

  if (badges.length === 0 && hasRequired && missing && missing.length === 0) {
    badges.push(
      <Badge
        key="ok"
        variant="outline"
        className="text-xs border-green-300 bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700 inline-flex items-center gap-1"
      >
        <CheckCircle2 className="w-3 h-3" />
        Skill verificate
      </Badge>
    );
  }

  const alternativesNode = renderAlternatives();

  if (badges.length === 0 && !alternativesNode) return null;

  return (
    <div className="flex flex-col gap-1">
      {badges.length > 0 && (
        <div className="flex flex-wrap items-center gap-1">{badges}</div>
      )}
      {alternativesNode}
    </div>
  );
}
